"use client";

import { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import { useGetFiles } from "../../../hooks/useGetFiles";
import { useStorageQuery } from "../../../hooks/storageQueryProvider";
import { usePersistedView } from "../../../hooks/usePersistedView";
import FilterByType from "./header/filterByType";
import SortBy from "./header/sortBy";
import OrderBy from "./header/orderBy";
import SelectAll from "./header/selectAll";
import FileCard from "./card/fileCard";
import FilesPagination from "./footer/pagination";
import FileCardSkeleton from "./card/fileCardSkeleton";
import View from "./header/view";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";

export default function FilesGrid({
    setIsNoFiles,
}: {
    setIsNoFiles: Dispatch<SetStateAction<boolean>>;
}) {
    const { filesQuery, parentFolder, settings } = useStorageQuery();

    const { data, isPending } = useGetFiles({
        ...filesQuery,
        folderId: parentFolder?.id,
    });

    const files = useMemo(() => data?.data, [data?.data]);
    const totalFiles = useMemo(() => data?.total, [data?.total]);
    const isFiltered = filesQuery.filterBy !== "all";
    const isEmpty = !isPending && !files?.length;

    // A type filter can hide every file. Only then ask whether the folder has
    // files of other types, so a truly empty folder still shows nothing.
    const { data: unfiltered } = useGetFiles(
        {
            ...filesQuery,
            filterBy: "all",
            page: 1,
            resultsPerPage: 1,
            folderId: parentFolder?.id,
        },
        { enabled: isFiltered && isEmpty }
    );
    const folderHasFiles = !isEmpty
        ? true
        : isFiltered
        ? unfiltered && unfiltered.total > 0
        : false;

    useEffect(() => {
        setIsNoFiles(folderHasFiles === false);
    }, [folderHasFiles, setIsNoFiles]);

    const [view, setView] = usePersistedView("files");

    const numOfSkeletons = Math.min(12, filesQuery.resultsPerPage);

    if (!folderHasFiles) {
        return null;
    }

    return (
        <div>
            <div className="flex flex-col gap-2 md:gap-0 md:flex-row justify-between md:items-center">
                <div className="flex flex-row gap-4">
                    <h3 className="text-xl font-semibold text-muted-foreground">
                        {t("storage.files-grid.title")}
                    </h3>
                </div>
                {!isPending && (
                    <div className="flex flex-col w-fit md:flex-row gap-2">
                        {settings.files?.initialFilterBy === "all" && (
                            <FilterByType />
                        )}
                        <SortBy />
                        <OrderBy />
                        {!isEmpty && <SelectAll files={files} />}
                        <View view={view} setView={setView} />
                    </div>
                )}
            </div>
            {isEmpty ? (
                <p className="mt-4 text-sm text-muted-foreground">
                    {t("storage.files-grid.empty-filtered")}
                </p>
            ) : (
                <div
                    className={cn(
                        "mt-4",
                        view === "grid" &&
                            "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4",
                        view === "list" &&
                            "flex w-full flex-col divide-y-2 rounded-md border"
                    )}
                >
                    {isPending
                        ? Array.from({ length: numOfSkeletons }, (_, i) => i).map(
                              (_, idx) => (
                                  <FileCardSkeleton key={idx} view={view} />
                              )
                          )
                        : files?.map((file) => (
                              <FileCard file={file} key={file.id} view={view} />
                          ))}
                </div>
            )}

            {!isPending && !isEmpty && (
                <FilesPagination totalNumberOfFiles={totalFiles || 0} />
            )}
        </div>
    );
}
